import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../../../../config/firebase';
import { validateShareAccess } from '../../../../../../utils/shareHelpers';
import { logError, logInfo } from '../../../../../../utils/logger';

const useSharedMapData = (shareId) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [pois, setPois] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [shareData, setShareData] = useState(null);

  useEffect(() => {
    const loadSharedMapData = async () => {
      if (!shareId) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        // Validate share access
        const { hasAccess, error, shareData } = await validateShareAccess(shareId);
        
        if (!hasAccess) {
          setError(error || 'Access denied');
          setLoading(false);
          return;
        }
        
        setShareData(shareData);
        
        // Get survey data
        const surveyId = shareData.surveyId;
        const surveyRef = collection(db, 'surveys');
        const surveyQuery = query(surveyRef, where('id', '==', surveyId));
        const surveySnapshot = await getDocs(surveyQuery);
        
        if (surveySnapshot.empty) {
          setError('Survey not found');
          setLoading(false);
          return;
        }
        
        const surveyDoc = surveySnapshot.docs[0];
        setSurveyData(surveyDoc.data());
        
        // Get POIs
        const poisRef = collection(db, 'pois');
        const poisQuery = query(poisRef, where('surveyId', '==', surveyId));
        const poisSnapshot = await getDocs(poisQuery);
        
        const poisData = poisSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPois(poisData);
        
        // Get routes
        const routesRef = collection(db, 'routes');
        const routesQuery = query(routesRef, where('surveyId', '==', surveyId));
        const routesSnapshot = await getDocs(routesQuery);
        
        const routesData = routesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRoutes(routesData);
        
        logInfo(`Loaded shared map data: ${poisData.length} POIs, ${routesData.length} routes`);
        setLoading(false);
      } catch (error) {
        logError('Failed to load shared map data:', error);
        setError('Failed to load map data. Please try again.');
        setLoading(false);
      }
    };

    loadSharedMapData();
  }, [shareId]);

  return {
    loading,
    error,
    surveyData,
    pois,
    routes,
    shareData
  };
};

export default useSharedMapData;